import { useCallback, useContext, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { getExtensionValue } from "@medplum/core";
import { Condition, Resource } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Card, Divider, Stack } from "@mantine/core";

import * as Sentry from "@sentry/react";

import { fetchAllergiesBadges } from "@/clients/zod-schemas/allergies";
import { fetchProblemsBadges } from "@/clients/zod-schemas/problems";
import { PatientContext } from "@/contexts/patient";
import { ProfileContext } from "@/contexts/profile";
import { Extensions } from "@/fhir/enums/extensions";
import { ConditionClinicalStatus } from "@/fhir/enums/fhir-valuesets";
import { ProblemType } from "@/fhir/enums/quilted-health";
import { useAppDispatch } from "@/store";
import { loadAllergies } from "@/store/slices/client-allergies";
import { loadProblems, mergeAndLoadProblemsFromPoll } from "@/store/slices/client-problems";
import { stopResourcePolling } from "@/store/slices/resource-polling";
import { PoolingKeys } from "@/types/pooling";
import { useResourcePolling } from "@/utils/hooks";

import ChecklistTodos from "../../ChecklistTodos";
import { ClientReminderNotes } from "../ClientNotes/ClientRemindersNote";

import ClientAllergies from "./ClientAllergies";
import ClientBloodType from "./ClientBloodType";
import ClientFlags from "./ClientFlags";
import classes from "./ClientHealthSummary.module.css";
import ClientMedications from "./ClientMedications";
import ClientProblems from "./ClientProblems";

export default function ClientHealthSummary() {
  const medplum = useMedplum();
  const { clientId } = useParams() as { clientId: string };

  const { patient } = useContext(PatientContext);
  const noReportedProblems =
    (getExtensionValue(patient, Extensions.NoReportedProblems) as boolean) ?? false;
  const noReportedAllergies =
    (getExtensionValue(patient, Extensions.NoReportedAllergies) as boolean) ?? false;
  const noReportedMedication =
    (getExtensionValue(patient, Extensions.NoReportedMedications) as boolean) ?? false;

  const { isPatient } = useContext(ProfileContext);

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  useResourcePolling(
    PoolingKeys.ClientProblems,
    "Condition",
    useMemo(
      () => ({
        _compartment: `Patient/${clientId}`,
        category: ConditionClinicalStatus.ProblemListItem,
      }),
      [clientId],
    ),
    useCallback(
      async (resources: Resource[]) => {
        dispatch(mergeAndLoadProblemsFromPoll(resources as Condition[]));
      },
      [dispatch],
    ),
    useCallback(
      (error: Error) => {
        Sentry.captureException(error);
        dispatch(stopResourcePolling(PoolingKeys.ClientProblems));
      },
      [dispatch],
    ),
    { interval: 3000 },
  );

  useEffect(() => {
    if (!patient) return;

    const fetchProblems = async () => {
      const problems = await fetchProblemsBadges(medplum, patient.id!);
      dispatch(loadProblems(problems));
    };

    const fetchAllergies = async () => {
      const allergies = await fetchAllergiesBadges(medplum, patient.id!);
      dispatch(loadAllergies(allergies));
    };

    fetchProblems();
    fetchAllergies();
  }, [patient, medplum, dispatch]);

  const handleClick = () => {
    navigate(`/clients/${patient?.id}/pam`);
  };

  return (
    <Card p={0} bg="white" className={classes.healthSummaryCard}>
      <Stack gap={0} className={classes.mobileS}>
        {!isPatient && <ClientFlags />}
        {!isPatient && <ClientReminderNotes />}
        <Divider color="cream.3" />
        <ChecklistTodos />
        <Divider color="cream.3" />
        <ClientBloodType />
        <Divider color="cream.3" />
        <ClientProblems
          title="Problems"
          onClick={handleClick}
          type={ProblemType.NonPregnancyProblem}
          noReportedProblems={noReportedProblems}
        />
        <Divider color="cream.3" />
        <ClientProblems
          onClick={handleClick}
          title="Pregnancy Problems"
          type={ProblemType.PregnancyProblem}
          noReportedProblems={noReportedProblems}
        />
        <Divider color="cream.3" />
        <ClientMedications onClick={handleClick} noReportedMedication={noReportedMedication} />
        <Divider color="cream.3" />
        <ClientAllergies onClick={handleClick} noReportedAllergies={noReportedAllergies} />
      </Stack>
    </Card>
  );
}
